import React, { useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Plus, Check, X, Building2, Store } from "lucide-react";

import { CommonTable } from "../../components/CommonTable";
import { Button } from "../../components/ui/Button";
import { useAppDispatch, useAppSelector } from "../../store/hooks/reduxHooks";

import {
  fetchMerchantApprovalsThunk,
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
} from "../../features/merchantApproval/merchantApprovalSlice";
import { MerchantApproval } from "../../services/merchantApproval";

import CreateMerchantApprovalDialog from "./CreateMerchantApprovalDialog";

const LenderMerchantApproval: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    merchantApprovals,
    loading,
    page,
    pageSize,
    total,
    sortBy,
    sortOrder,
    searchQuery,
    hasFetched,
  } = useAppSelector((state) => state.merchantApproval);

  const { token } = useAppSelector((state) => state.auth);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Seed from Redux so the input matches the table when the tab is revisited
  const [localSearch, setLocalSearch] = useState(searchQuery);

  // Snapshot of the params used for the last fetch. Seeded from the current
  // Redux values on mount, so a revisit sees "no change" and does not refetch.
  const prevRef = useRef({ page, pageSize, sortBy, sortOrder, searchQuery });

  /**
   * 1. First hit only: fetch if the store has never been loaded.
   *    On revisit hasFetched is already true, so nothing is called.
   */
  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;

    dispatch(fetchMerchantApprovalsThunk());
  }, [token, hasFetched, dispatch]);

  /**
   * 2. Refetch only when page / size / sort / search actually change
   *    compared with the last fetch.
   */
  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder ||
      prev.searchQuery !== searchQuery;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchMerchantApprovalsThunk());
      prevRef.current = { page, pageSize, sortBy, sortOrder, searchQuery };
    }, 300);

    return () => clearTimeout(timeout);
  }, [page, pageSize, sortBy, sortOrder, searchQuery, hasFetched, dispatch]);

  // Search
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);

    if (!value.trim()) {
      dispatch(setPage(1));
      dispatch(setSearchQuery(""));
    }
  };

  const handleSearchSubmit = () => {
    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch.trim()));
  };

  const handleResetSearch = () => {
    setLocalSearch("");
    dispatch(setPage(1));
    dispatch(setSearchQuery(""));
  };

  /**
   * Refresh: explicit refetch with the current params.
   * Keep the snapshot in sync so effect 2 doesn't fire a duplicate.
   */
  const handleRefresh = () => {
    prevRef.current = { page, pageSize, sortBy, sortOrder, searchQuery };
    dispatch(fetchMerchantApprovalsThunk());
  };

  /**
   * After create: newest first, page 1, fetch once.
   * State updates are synchronous, so the thunk sees the new values;
   * the snapshot is updated so effect 2 doesn't fetch again.
   */
  const handleMerchantApprovalCreated = () => {
    setIsCreateDialogOpen(false);

    dispatch(setSortBy("createdAt"));
    dispatch(setSortOrder("desc"));
    dispatch(setPage(1));

    prevRef.current = {
      page: 1,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "desc",
      searchQuery,
    };

    dispatch(fetchMerchantApprovalsThunk());
  };

  const columns = useMemo<ColumnDef<MerchantApproval>[]>(
    () => [
      {
        id: "serialNo",
        header: "S.No",
        cell: ({ row }) => (
          <div className="text-gray-600">{(page - 1) * pageSize + row.index + 1}</div>
        ),
        enableSorting: false,
        size: 50,
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-gray-900">{row.original.name}</span>
          </div>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "legalName",
        header: "Legal Name",
        cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.legalName}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.category}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "presence",
        header: "Presence",
        cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.presence}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "centers",
        header: "Centers",
        cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.centers ?? 0}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "avgAnnualRevenue",
        header: "Avg Annual Revenue",
        cell: ({ row }) => (
          <span className="text-sm text-gray-700">
            ₹{(row.original.avgAnnualRevenue ?? 0).toLocaleString("en-IN")}
          </span>
        ),
        enableSorting: true,
      },
      {
        // Not in the backend's allowedSortFields, so sorting is disabled
        accessorKey: "nabhApproved",
        header: "NABH Approved",
        cell: ({ row }) => {
          const approved = row.original.nabhApproved;
          return (
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                approved ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}
            >
              {approved ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
              {approved ? "Yes" : "No"}
            </span>
          );
        },
        enableSorting: false,
      },
      {
  accessorKey: "status",
  header: "Status",
  cell: ({ row }) => {
    const status = row.original.status;
    const remark = row.original.remark?.trim();

    const isRejected = status === "REJECTED";
    const isUnderReview = status === "UNDER_REVIEW";

    const statusClass =
      status === "APPROVED"
        ? "bg-green-100 text-green-700"
        : isRejected
        ? "bg-red-100 text-red-700"
        : "bg-yellow-100 text-yellow-700";

    const statusLabel =
      status === "APPROVED"
        ? "Approved"
        : isRejected
        ? "Rejected"
        : "Under Review";

    return (
      <div className="flex flex-col gap-1.5 max-w-[280px]">
        {/* Status */}
        <span
          className={`inline-flex w-fit items-center px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}
        >
          {statusLabel}
        </span>

        {/* Lender Remark */}
        {(isRejected || isUnderReview) && remark && (
          <div className="text-xs text-gray-600 leading-4">
            <span className="font-medium text-gray-700">
              Lender Remark:
            </span>{" "}
            {remark}
          </div>
        )}
      </div>
    );
  },
  enableSorting: true,
},
      {
        accessorKey: "lender",
        header: "Lender",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">{row.original.lender}</span>
          </div>
        ),
        enableSorting: true,
      },
    ],
    [page, pageSize]
  );

  return (
    <div className="w-full no-scrollbar">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Lender Merchant Approval</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage merchants submitted for lender approval.
          </p>
        </div>

        <Button onClick={() => setIsCreateDialogOpen(true)} leftIcon={<Plus size={16} />}>
          Add Merchant Approval
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={merchantApprovals}
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(newPage) => dispatch(setPage(newPage))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}
          searchValue={localSearch}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          isSearchDisabled={!localSearch.trim()}
          searchError={null}
          onResetSearch={handleResetSearch}
          onRefresh={handleRefresh}
          searchPlaceholder="Search merchant approvals..."
          enableRowClick={false}
          emptyStateMessage="No merchant approvals found."
        />
      </div>

      <CreateMerchantApprovalDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreated={handleMerchantApprovalCreated}
      />
    </div>
  );
};

export default LenderMerchantApproval;